import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { fetchBlogPosts, fetchBlogTags } from '../lib/api';
import type { BlogPost, BlogTag } from '../types';

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-NZ', { day: 'numeric', month: 'long', year: 'numeric' });
}

function TagChip({ tag, active, onClick }: { tag: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
        active
          ? 'bg-accent text-white border-accent'
          : 'bg-surface-light dark:bg-surface text-text-secondary-light dark:text-text-secondary border-[rgba(79,179,217,0.25)] hover:border-accent hover:text-accent'
      }`}
    >
      {tag}
    </button>
  );
}

function FeatureCard({ post }: { post: BlogPost }) {
  return (
    <Link
      to={`/news/${post.slug}`}
      className="news-card group md:col-span-6 grid md:grid-cols-2 bg-card-light dark:bg-card border border-[rgba(16,42,66,0.09)] dark:border-[rgba(214,233,247,0.09)] rounded-xl overflow-hidden"
    >
      <div className="overflow-hidden aspect-[16/10] md:aspect-auto md:min-h-[340px]">
        <img
          src={post.hero_image}
          alt={post.title}
          loading="eager"
          className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
        />
      </div>
      <div className="p-6 md:p-8 flex flex-col justify-center gap-3">
        <div className="flex flex-wrap gap-2">
          {post.tags.slice(0, 3).map(t => (
            <span key={t} className="text-[0.65rem] font-semibold uppercase tracking-wider text-accent">{t}</span>
          ))}
        </div>
        <h2 className="font-display text-xl md:text-3xl font-bold tracking-tight leading-tight group-hover:text-accent transition-colors">
          {post.title}
        </h2>
        <p className="text-text-secondary-light dark:text-text-secondary text-sm md:text-base leading-relaxed">{post.excerpt}</p>
        <span className="text-xs text-text-muted mt-1">{formatDate(post.published_at)} · {post.author}</span>
      </div>
    </Link>
  );
}

function MosaicCard({ post, large }: { post: BlogPost; large?: boolean }) {
  return (
    <Link
      to={`/news/${post.slug}`}
      className={`news-card group bg-card-light dark:bg-card border border-[rgba(16,42,66,0.09)] dark:border-[rgba(214,233,247,0.09)] rounded-xl overflow-hidden flex flex-col ${
        large ? 'md:col-span-3' : 'md:col-span-2'
      }`}
    >
      <div className={`overflow-hidden ${large ? 'aspect-[16/9]' : 'aspect-[16/10]'}`}>
        <img
          src={post.hero_image}
          alt={post.title}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
        />
      </div>
      <div className="p-4 md:p-5 flex flex-col gap-2 flex-1">
        <div className="flex flex-wrap gap-2">
          {post.tags.slice(0, 2).map(t => (
            <span key={t} className="text-[0.6rem] font-semibold uppercase tracking-wider text-accent">{t}</span>
          ))}
        </div>
        <h3 className={`font-display font-bold tracking-tight leading-snug group-hover:text-accent transition-colors ${
          large ? 'text-lg md:text-xl' : 'text-base'
        }`}>
          {post.title}
        </h3>
        {large && (
          <p className="text-text-secondary-light dark:text-text-secondary text-sm leading-relaxed line-clamp-3">{post.excerpt}</p>
        )}
        <span className="text-[0.7rem] text-text-muted mt-auto pt-1">{formatDate(post.published_at)}</span>
      </div>
    </Link>
  );
}

export default function MarineNews() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [tags, setTags] = useState<BlogTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTag = searchParams.get('tag') || '';

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchBlogPosts(activeTag || undefined),
      fetchBlogTags(),
    ]).then(([p, t]) => {
      setPosts(p);
      setTags(t);
      setLoading(false);
    });
  }, [activeTag]);

  const setTag = (tag: string) => {
    if (tag) setSearchParams({ tag });
    else setSearchParams({});
  };

  const [feature, ...rest] = posts;

  return (
    <div className="max-w-[1440px] mx-auto px-4 md:px-8 pt-10 md:pt-16 pb-16">
      <div className="flex items-center gap-2 text-sm text-text-muted mb-4">
        <Link to="/" className="text-text-secondary hover:text-accent transition-colors">Home</Link>
        <span>/</span>
        <span>Marine News</span>
      </div>

      <div className="border-b border-[rgba(79,179,217,0.15)] dark:border-[rgba(79,179,217,0.2)] pb-5 mb-6">
        <h1 className="font-display text-2xl md:text-[2.75rem] font-bold tracking-tight">Marine News</h1>
        <p className="text-text-secondary-light dark:text-text-secondary max-w-[640px] mt-2 text-sm md:text-base">
          New Zealand marine industry news - boats, boatbuilding, sailing, fishing, aquaculture and coastal life. Researched daily, written for boaties.
        </p>
      </div>

      {tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-8">
          <TagChip tag="All" active={!activeTag} onClick={() => setTag('')} />
          {tags.slice(0, 14).map(t => (
            <TagChip key={t.tag} tag={`${t.tag} (${t.count})`} active={activeTag === t.tag} onClick={() => setTag(t.tag)} />
          ))}
        </div>
      )}

      {loading ? (
        <div className="py-20 text-center text-text-muted text-sm">Loading the latest…</div>
      ) : posts.length === 0 ? (
        <div className="py-20 text-center">
          <p className="text-text-secondary-light dark:text-text-secondary">No stories yet{activeTag ? ` tagged “${activeTag}”` : ''}.</p>
          <p className="text-text-muted text-sm mt-2">Our research desk scans NZ marine news daily - check back soon.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 md:gap-5">
          {feature && <FeatureCard post={feature} />}
          {rest.map((p, i) => (
            <MosaicCard key={p.id} post={p} large={i < 2} />
          ))}
        </div>
      )}
    </div>
  );
}
